const { sdkFetch } = require('./http');
const { SDK_FOPS } = require('./variables');
const { applePayConfigService, mainConfigService } = require('./services/config');
const { APPLE_PAY_NETWORK_VERSIONS } = require('./constants');

let callbackService = undefined;


function initializeApplePayServices(cbService) {
  callbackService = cbService;
}

exports.initializeApplePayServices = initializeApplePayServices;

/**
 * Checks if Apple Pay is possible in the current environment.
 * @return {boolean} Boolean to check if Apple Pay is possible
 */
const _isWalletActive = async () => {
  try {
    return await window.ApplePaySession.canMakePaymentsWithActiveCard(
      applePayConfigService.get('merchant_id')
    );
  } catch (error) {
    return false;
  }
};
exports._isWalletActive = _isWalletActive;

const _applePayAvailable = function () {
  try {
    if (window.ApplePaySession && window.ApplePaySession.canMakePayments()) {
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
};
exports._applePayAvailable = _applePayAvailable;

const _openAppleWalletApp = async function (merchantIdentifier) {
  const errMessage = 'Failed to open wallet app.';
  try {
    const response = await window.ApplePaySession.openPaymentSetup(merchantIdentifier);
    if (!response) {
      callbackService.callBackError({
        status: 'error',
        form_of_payment: SDK_FOPS.APPLE_PAY,
        message: errMessage,
      });
    }
  } catch (err) {
    callbackService.callBackError({
      status: 'error',
      form_of_payment: SDK_FOPS.APPLE_PAY,
      message: err.message || errMessage,
    });
  }
};
exports._openAppleWalletApp = _openAppleWalletApp;

/**
 * Filters networks to only include those supported by the given Apple Pay version
 * @param {Array<string>} networks - List of network identifiers
 * @param {number} applePayVersion - Resolved Apple Pay version
 * @returns {Array<string>} Filtered networks that are compatible with the version
 */
const _filterNetworksByVersion = function (networks, applePayVersion) {
  return networks.filter(network => {
    const requiredVersion = APPLE_PAY_NETWORK_VERSIONS[network] || 1;
    const isSupported = requiredVersion <= applePayVersion;

    if (!isSupported) {
      console.warn(
        `[Apple Pay] Excluding network "${network}" - requires version ${requiredVersion}, device supports version ${applePayVersion}`
      );
    }

    return isSupported;
  });
};

/**
 * Attempts to create an Apple Pay session with version-aware network filtering
 * @param {number} resolvedVersion - The Apple Pay version being used
 * @param {object} paymentRequest - The payment request object
 * @returns {ApplePaySession} The created Apple Pay session
 */
const _createApplePaySession = function (resolvedVersion, paymentRequest) {
  const originalNetworks = paymentRequest.supportedNetworks;
  let versionCompatibleNetworks = _filterNetworksByVersion(originalNetworks, resolvedVersion);

  if (versionCompatibleNetworks.length === 0) {
    throw new Error('No compatible payment networks available for the current Apple Pay version');
  }

  let remainingNetworks = [...versionCompatibleNetworks];
  const removedNetworks = [];
  let lastError = null;

  while (remainingNetworks.length > 0) {
    const filteredRequest = {
      ...paymentRequest,
      supportedNetworks: remainingNetworks
    };

    try {
      const session = new window.ApplePaySession(resolvedVersion, filteredRequest);

      return session;
    } catch (error) {
      lastError = error;

      if (error.message && error.message.includes('not a valid payment network')) {
        // Extract the invalid network name from error message
        const match = error.message.match(/"([^"]+)" is not a valid payment network/);
        const invalidNetwork = match ? match[1] : null;

        if (invalidNetwork) {
          console.warn(`[Apple Pay] Network "${invalidNetwork}" is not valid, removing and retrying`);

          remainingNetworks = remainingNetworks.filter(net => net !== invalidNetwork);
          removedNetworks.push(invalidNetwork);

          continue;
        }
      }

      // If it's a different error that we can't handle, break out of loop
      break;
    }
  }


  throw lastError || new Error('Failed to create Apple Pay session');
};

const _startApplePaySession = async function () {
  const paymentRequest = {};

  let fee_description = undefined;
  if (applePayConfigService.get('fee') > 0) {
    fee_description = {
      label: applePayConfigService.get('fee_description') || 'Fee',
      amount: `${parseFloat(applePayConfigService.get('fee'))}`,
      type: 'final',
    };
  }
  (paymentRequest.currencyCode = applePayConfigService.get('currency_code')),
  (paymentRequest.countryCode = applePayConfigService.get('country_code')),
  (paymentRequest.merchantCapabilities =
      applePayConfigService.get('merchantCapabilities') &&
      applePayConfigService.get('merchantCapabilities').length
        ? applePayConfigService.get('merchantCapabilities')
        : ['supports3DS']),
  (paymentRequest.supportedNetworks =
      applePayConfigService.get('supportedNetworks') &&
      applePayConfigService.get('supportedNetworks').length
        ? applePayConfigService.get('supportedNetworks')
        : ['amex', 'masterCard', 'maestro', 'visa', 'mada']),
  (paymentRequest.supportedCountries =
      applePayConfigService.get('supportedCountries') &&
      applePayConfigService.get('supportedCountries').length
        ? applePayConfigService.get('supportedCountries')
        : undefined),
  (paymentRequest.total = {
    label: applePayConfigService.get('shop_name'),
    amount: applePayConfigService.get('amount'),
    type: 'final',
  }),
  (paymentRequest.lineItems =
      fee_description &&
      applePayConfigService.get('lineItems') &&
      applePayConfigService.get('lineItems').length
        ? [fee_description, ...applePayConfigService.get('lineItems')]
        : fee_description
          ? [fee_description]
          : undefined);

  
  try {
    const resolvedVersion = applePayConfigService.resolveVersion();

    var applePaySession = _createApplePaySession(resolvedVersion, paymentRequest);

    const sessionPromise = _handleApplePayEvents(applePaySession);

    applePaySession.begin();
    return sessionPromise;
  } catch (error) {
    return Promise.resolve(); // Return resolved promise on error to unlock the button
  }
};
exports._startApplePaySession = _startApplePaySession;
exports._createApplePaySession = _createApplePaySession;
exports._filterNetworksByVersion = _filterNetworksByVersion;

const _handleApplePayEvents = function (appleSession) {
  return new Promise((resolve) => {
    const _applePayAbort = () => {
      try {
        appleSession.abort();
      } catch (error) {
        console.log('Error: ApplePay abort', error);
      }
    };

    const _applePayCompletePayment = (result) => {
      try {
        appleSession.completePayment(result);
      } catch (error) {
        console.log('Error: ApplePay completePayment', error);
      }
    };

    const _applePayCompleteValidation = (sessionDetails) => {
      try {
        appleSession.completeMerchantValidation(sessionDetails);
      } catch (error) {
        console.log('Error: ApplePay completeMerchantValidation', error);
      }
    };

    // Handle when user cancels the Apple Pay sheet
    appleSession.oncancel = function () {
      resolve(); // Resolve (not reject) as cancellation is a normal user action
    };

    appleSession.onvalidatemerchant = function (event) {
      _validateApplePaySession(event.validationURL, function (merchantSession) {
        if (
          merchantSession.callback_payload &&
        merchantSession.callback_payload.status == 'error'
        ) {
          _applePayAbort();
          callbackService.callBackError(merchantSession.callback_payload);
          resolve();
        } else if (merchantSession.err !== undefined) {
          _applePayAbort();
          callbackService.callBackError({
            status: 'error',
            form_of_payment: SDK_FOPS.APPLE_PAY,
            message: merchantSession.err,
          });
          resolve();
        }

        callbackService.callBackPayment()
          .then(() => {
            _applePayCompleteValidation(merchantSession);
          })
          .catch(() => {
            _applePayAbort();
            resolve();
          });
      });
    };

    appleSession.onpaymentauthorized = function (event) {
      _performTransaction(event.payment, function (outcome) {
        if (outcome.approved) {
          _applePayCompletePayment({
            status: window.ApplePaySession.STATUS_SUCCESS,
          });
          if (outcome.callback_payload) {
            callbackService.callBackSuccess(outcome.callback_payload);
          } else {
            callbackService.callBackSuccess({
              status: 'success',
              message: outcome.message || '',
              ...outcome,
            });
          }
          resolve();
        } else if (!outcome.approved && !outcome.err) {
          const err = new window.ApplePayError('unknown');
          _applePayCompletePayment({
            status: window.ApplePaySession.STATUS_FAILURE,
            errors: [err],
          });
          if (
            outcome.callback_payload &&
          outcome.callback_payload.status == 'canceled'
          ) {
            if (
              outcome.callback_payload.payment_gateway_info.pg_name === 'kpay'
            ) {
              appleSession.abort();
            }
            callbackService.callBackCancel(outcome.callback_payload);
          } else if (
            outcome.callback_payload &&
          outcome.callback_payload.status == 'error'
          ) {
            callbackService.callBackError(outcome.callback_payload);
          }
          resolve();
        } else if (outcome.err) {
          const err = new window.ApplePayError('unknown');
          _applePayCompletePayment({
            status: window.ApplePaySession.STATUS_FAILURE,
            errors: [err],
          });
          callbackService.callBackError({
            status: 'error',
            form_of_payment: SDK_FOPS.APPLE_PAY,
            message: outcome.err,
            ...outcome,
          });
          resolve();
        } else {
          _applePayCompletePayment(window.ApplePaySession.STATUS_FAILURE);
          callbackService.callBackError({
            status: 'error',
            form_of_payment: SDK_FOPS.APPLE_PAY,
            message: outcome.message,
            ...outcome,
          });
          resolve();
        }
      });
    };
  });
};

const _validateApplePaySession = async function (appleUrl, callback) {
  const url = applePayConfigService.get('validation_url');
  try {
    const response = await sdkFetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Api-Key ${mainConfigService.get('apiKey')}`,
      },
      body: JSON.stringify({
        apple_url: appleUrl,
        session_id: mainConfigService.get('session_id'),
        code: applePayConfigService.get('gateway_code'),
      }),
    });

    if (response.ok) {
      const data = await response.json();
      callback(data);
    } else {
      const data = await response.json();
      if (data.callback_payload) {
        callback(data);
      } else {
        throw new Error(response.status + ' Failed merchant validation ');
      }
    }
  } catch (err) {
    callback({ err });
  }
};

const _performTransaction = async function (details, callback) {
  try {
    const response = await sdkFetch(applePayConfigService.get('payment_url'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Api-Key ${mainConfigService.get('apiKey')}`,
      },
      body: JSON.stringify({
        apple_pay_payload: {
          token: details,
        },
        currency_code: applePayConfigService.get('currency_code'),
        amount: applePayConfigService.get('amount'),
        session_id: mainConfigService.get('session_id'),
        code: applePayConfigService.get('gateway_code'),
      }),
    });

    if (response.ok) {
      const data = await response.json();
      callback(data);
    } else {
      const data = await response.json();
      if (data.callback_payload) {
        callback(data);
      } else {
        throw new Error(response.status + ' Failed Fetch ');
      }
    }
  } catch (err) {
    callback({ err });
  }
};

const APPLE_PAY_SDK_SRC = 'https://applepay.cdn-apple.com/jsapi/v1.3.2/apple-pay-sdk.js';

const loadApplePayJS = async () => {
  return new Promise((resolve, reject) => {
    if (window.ApplePaySession) {
      const sdkScriptInDOM = !!document.querySelector(`script[src="${APPLE_PAY_SDK_SRC}"]`);
      if (sdkScriptInDOM) {
        return resolve({ appleScriptLoaded: false }); // SDK-provided (Chrome)
      }
      return resolve({ appleScriptLoaded: true }); // Native Safari
    }

    const script = document.createElement('script');
    script.src = APPLE_PAY_SDK_SRC;
    script.integrity =
      'sha384-DZRWMZLyVXr+7shJfal8pIG2v4KisLoSWFjZQMUv0+GWaCwoa82qeHsWrbBIUDPU';
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve({ appleScriptLoaded: false });
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

exports.loadApplePayJS = loadApplePayJS;

// Workaround to allow dynamically adding apple-pay-button
const embedPlaceHolderApplePayButton = () => {
  if (window.ApplePaySession && !document.querySelector('apple-pay-button[data-sdk-placeholder]')) {
    const btn = document.createElement('apple-pay-button');
    btn.setAttribute('buttonstyle', 'black');
    btn.setAttribute('type', 'plain');
    btn.setAttribute('locale', 'en-US');
    btn.setAttribute('data-sdk-placeholder', 'true');
    btn.style.display = 'none'; // forces the SDK to render others properly
    document.body.appendChild(btn);
  }
};

exports.embedPlaceHolderApplePayButton = embedPlaceHolderApplePayButton;
